import { useEffect, useState } from 'react';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { X, Receipt, Percent, Calendar, ShoppingCart } from 'lucide-react';

type PurchaseOrder = {
  id: number;
  order_number?: string;
  Client?: {
    company_name?: string;
  };
  total_amount?: number;
  status?: string;
};

type CreateInvoiceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preselectedPurchaseOrderId?: number;
};

export const CreateInvoiceModal = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedPurchaseOrderId,
}: CreateInvoiceModalProps) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState<number | null>(preselectedPurchaseOrderId || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [percentage, setPercentage] = useState(100);
  const [dueDays, setDueDays] = useState(30);
  const [notes, setNotes] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Paiement à 30 jours');

  // Charger les commandes d'achat
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(async () => {
      try {
        const q = searchTerm.trim();
        // Utiliser le bon endpoint pour les commandes d'achat
        const res = await apiService.get('/purchase-orders', {
          params: { search: q || undefined, limit: 50 }
        });
        console.log('Purchase orders response:', res);
        console.log('Response data:', res.data);
        console.log('Response data type:', typeof res.data);
        console.log('Is res.data array?', Array.isArray(res.data));
        
        // Try different possible response structures
        let orders = [];
        if (Array.isArray(res.data)) {
          orders = res.data;
          console.log('Using res.data directly');
        } else if (res.data && Array.isArray(res.data.data)) {
          orders = res.data.data;
          console.log('Using res.data.data');
        } else if (res.data && Array.isArray(res.data.items)) {
          orders = res.data.items;
          console.log('Using res.data.items');
        } else if (Array.isArray(res)) {
          orders = res;
          console.log('Using res directly');
        } else {
          console.log('No valid array found in response');
        }
        
        console.log('Final parsed orders:', orders);
        console.log('Orders count:', orders.length);
        setPurchaseOrders(orders);
      } catch (e: any) {
        toast({ title: 'Erreur', description: 'Impossible de charger les commandes.', variant: 'destructive' });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isOpen, searchTerm, toast]);

  const handleCreate = async () => {
    if (!selectedPurchaseOrderId) {
      toast({ title: 'Commande requise', description: 'Veuillez sélectionner une commande.', variant: 'destructive' });
      return;
    }

    if (percentage < 10 || percentage > 100) {
      toast({ title: 'Pourcentage invalide', description: 'Le pourcentage doit être entre 10% et 100%.', variant: 'destructive' });
      return;
    }

    try {
      setBusy(true);

      // Créer facture depuis commande d'achat
      await apiService.post('/invoices/from-purchase-order', {
        purchase_order_id: selectedPurchaseOrderId,
        percentage,
        due_days: dueDays,
        notes: notes || undefined,
        payment_terms: paymentTerms || undefined,
      });

      toast({ 
        title: 'Facture créée', 
        description: `La facture a été créée avec succès (${percentage}% de la commande).` 
      });
      
      onSuccess?.();
      onClose();
      
      // Reset form
      setSelectedPurchaseOrderId(preselectedPurchaseOrderId || null);
      setPercentage(100);
      setDueDays(30);
      setNotes('');
      setPaymentTerms('Paiement à 30 jours');
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Création impossible';
      toast({ title: 'Erreur', description: String(msg), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const getPurchaseOrderLabel = (po: PurchaseOrder) => {
    const num = po.order_number || `PO-${po.id}`;
    const company = po.Client?.company_name || '';
    const total = po.total_amount ? `${po.total_amount.toFixed(2)} €` : '';
    const status = po.status ? `[${po.status.toUpperCase()}]` : '';
    return [num, company, total, status].filter(Boolean).join(' — ');
  };

  const selectedPurchaseOrder = purchaseOrders.find(po => po.id === selectedPurchaseOrderId);
  const estimatedAmount = selectedPurchaseOrder?.total_amount ? (selectedPurchaseOrder.total_amount * percentage / 100).toFixed(2) : '0.00';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <Receipt className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Créer une Facture</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Générer une facture depuis une commande d'achat</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 dark:hover:bg-slate-800 rounded-lg transition-colors"
            disabled={busy}
          >
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Purchase Order Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
              Commande source <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une commande..."
              disabled={busy}
              className="w-full h-10 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
            />

            <div className="space-y-2 max-h-48 overflow-auto border border-slate-200 dark:border-slate-700 rounded-lg">
              {purchaseOrders.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune commande trouvée</p>
                </div>
              ) : (
                purchaseOrders.map((po) => (
                  <button
                    key={po.id}
                    onClick={() => setSelectedPurchaseOrderId(po.id)}
                    disabled={busy}
                    className={`w-full text-left p-3 transition-all border-b border-slate-100 dark:border-slate-800 last:border-0 ${
                      selectedPurchaseOrderId === po.id
                        ? 'bg-blue-50 dark:bg-blue-950/30 border-l-4 border-l-blue-500'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    } disabled:opacity-50`}
                  >
                    <h4 className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
                      {getPurchaseOrderLabel(po)}
                    </h4>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Percentage Slider */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <Percent className="h-4 w-4 text-amber-600" />
              Pourcentage de facturation
            </label>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-amber-600">{percentage}%</span>
                {selectedPurchaseOrder && (
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Montant estimé: <span className="font-bold text-amber-600">{estimatedAmount} €</span>
                  </span>
                )}
              </div>
              <input
                type="range"
                min="10"
                max="100"
                step="10"
                value={percentage}
                onChange={(e) => setPercentage(Number(e.target.value))}
                disabled={busy}
                className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-amber-600 disabled:opacity-50"
              />
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                <span>10%</span>
                <span>20%</span>
                <span>30%</span>
                <span>40%</span>
                <span>50%</span>
                <span>60%</span>
                <span>70%</span>
                <span>80%</span>
                <span>90%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          {/* Due Days */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-amber-600" />
              Échéance (jours)
            </label>
            <input
              type="number"
              value={dueDays}
              onChange={(e) => setDueDays(Number(e.target.value))}
              min="0"
              max="365"
              disabled={busy}
              className="w-full h-10 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
            />
          </div>

          {/* Payment Terms */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
              Conditions de paiement
            </label>
            <input
              type="text"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="Ex: Paiement à 30 jours"
              disabled={busy}
              className="w-full h-10 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
            />
          </div>

          {/* Notes */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
              Notes (optionnelles)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes additionnelles pour la facture..."
              disabled={busy}
              rows={3}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <div className="text-sm text-slate-600 dark:text-slate-400">
            {selectedPurchaseOrderId ? (
              <span className="flex items-center gap-2 text-amber-600 dark:text-amber-400 font-medium">
                <Receipt className="h-4 w-4" />
                Prêt à créer
              </span>
            ) : (
              'Sélectionnez une commande'
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Annuler
            </Button>
            <Button
              onClick={handleCreate}
              disabled={busy || !selectedPurchaseOrderId}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
            >
              {busy ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Création...
                </>
              ) : (
                <>
                  <Receipt className="h-4 w-4 mr-2" />
                  Créer la Facture
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
